import { useState, type FormEvent } from "react";
import { useAuth, useErrorHandler } from "../auth/AuthContext";
import { BuildInfo } from "../components/BuildInfo";
import { ErrorMessage, Field, Segmented } from "../components/common";

type Mode = "parent" | "child" | "register" | "join";

/** Login and family registration, with a kid-friendly path alongside. */
export function LoginPage() {
  const [mode, setMode] = useState<Mode>("parent");

  return (
    <div className="center-screen">
      <div className="page page--narrow">
        <div className="stack" style={{ textAlign: "center" }}>
          <h1>💰 Allowance</h1>
          <p className="muted">Keep track of your money together.</p>
        </div>

        <div className="card stack">
          <Segmented<Mode>
            value={mode === "register" || mode === "join" ? "parent" : mode}
            onChange={setMode}
            options={[
              { value: "parent", label: "Parent" },
              { value: "child", label: "Kid" },
            ]}
          />

          {mode === "child" ? (
            <>
              <p className="muted" style={{ textAlign: "center" }}>
                The family code is the household's sign-in name for kids — a
                parent finds it on their dashboard and shares it with you.
              </p>
              <ChildLoginForm />
            </>
          ) : mode === "register" ? (
            <RegisterForm onCancel={() => setMode("parent")} />
          ) : mode === "join" ? (
            <JoinFamilyForm onCancel={() => setMode("parent")} />
          ) : (
            <ParentLoginForm
              onRegister={() => setMode("register")}
              onJoin={() => setMode("join")}
            />
          )}
        </div>
      </div>

      {/* The login screen is where "is the server even up?" gets asked, so the
          footer lives here rather than inside the app shell — and stays out of
          the kid-facing dashboards, which have no use for a git SHA. */}
      <BuildInfo />
    </div>
  );
}

function ParentLoginForm({
  onRegister,
  onJoin,
}: {
  onRegister: () => void;
  onJoin: () => void;
}) {
  const { signIn } = useAuth();
  const toMessage = useErrorHandler();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await signIn(email, password);
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="stack" onSubmit={submit}>
      <ErrorMessage>{error}</ErrorMessage>

      <Field label="Email">
        <input
          className="input"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </Field>

      <Field label="Password">
        <input
          className="input"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
      </Field>

      <button
        type="submit"
        className="btn btn--primary btn--block"
        disabled={busy}
      >
        {busy ? "Signing in…" : "Sign in"}
      </button>

      <p className="muted" style={{ textAlign: "center" }}>
        New here?{" "}
        <button type="button" className="link-button" onClick={onRegister}>
          Set up your family
        </button>
      </p>
      <p className="muted" style={{ textAlign: "center" }}>
        Got an invite code from another parent?{" "}
        <button type="button" className="link-button" onClick={onJoin}>
          Join their family
        </button>
      </p>
    </form>
  );
}

function ChildLoginForm() {
  const { signInChild } = useAuth();
  const toMessage = useErrorHandler();

  const [familySlug, setFamilySlug] = useState("");
  const [username, setUsername] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await signInChild(familySlug, username, pin);
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="stack" onSubmit={submit}>
      <ErrorMessage>{error}</ErrorMessage>

      <Field label="Family code" hint="Ask a parent — it looks like smith-k4m9">
        <input
          className="input"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          placeholder="smith-k4m9"
          value={familySlug}
          onChange={(e) => setFamilySlug(e.target.value)}
          required
        />
      </Field>

      <Field label="Your name">
        <input
          className="input"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
        />
      </Field>

      <Field label="PIN">
        <input
          className="input"
          type="password"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={8}
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          required
        />
      </Field>

      <button
        type="submit"
        className="btn btn--primary btn--block"
        disabled={busy}
      >
        {busy ? "Checking…" : "Let me in"}
      </button>
    </form>
  );
}

function RegisterForm({ onCancel }: { onCancel: () => void }) {
  const { register } = useAuth();
  const toMessage = useErrorHandler();

  const [familyName, setFamilyName] = useState("");
  const [parentName, setParentName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [registrationCode, setRegistrationCode] = useState("");
  const [familySlug, setFamilySlug] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await register({
        familyName,
        parentName,
        email,
        password,
        ...(registrationCode ? { registrationCode } : {}),
        ...(familySlug ? { familySlug } : {}),
      });
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="stack" onSubmit={submit}>
      <ErrorMessage>{error}</ErrorMessage>

      <Field label="Family name">
        <input
          className="input"
          placeholder="The Smiths"
          value={familyName}
          onChange={(e) => setFamilyName(e.target.value)}
          required
        />
      </Field>

      <Field label="Your name">
        <input
          className="input"
          value={parentName}
          onChange={(e) => setParentName(e.target.value)}
          required
        />
      </Field>

      <Field label="Email">
        <input
          className="input"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </Field>

      <Field label="Password" hint="At least 8 characters">
        <input
          className="input"
          type="password"
          autoComplete="new-password"
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
      </Field>

      <Field
        label="Invite code"
        hint="Only needed if this server requires one"
      >
        <input
          className="input"
          value={registrationCode}
          onChange={(e) => setRegistrationCode(e.target.value)}
        />
      </Field>

      <Field
        label="Family code"
        hint="What kids type to sign in — leave blank for a random one, or pick your own. You can change it later."
      >
        <input
          className="input"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          placeholder="smith-k4m9"
          value={familySlug}
          onChange={(e) => setFamilySlug(e.target.value)}
        />
      </Field>

      <button
        type="submit"
        className="btn btn--primary btn--block"
        disabled={busy}
      >
        {busy ? "Creating…" : "Create our family"}
      </button>

      <p className="muted" style={{ textAlign: "center" }}>
        <button type="button" className="link-button" onClick={onCancel}>
          Back to sign in
        </button>
      </p>
    </form>
  );
}

function JoinFamilyForm({ onCancel }: { onCancel: () => void }) {
  const { joinFamily } = useAuth();
  const toMessage = useErrorHandler();

  const [code, setCode] = useState("");
  const [parentName, setParentName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await joinFamily({ code, parentName, email, password });
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="stack" onSubmit={submit}>
      <ErrorMessage>{error}</ErrorMessage>

      <Field
        label="Invite code"
        hint="From the parent who already has an account there"
      >
        <input
          className="input"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          required
        />
      </Field>

      <Field label="Your name">
        <input
          className="input"
          value={parentName}
          onChange={(e) => setParentName(e.target.value)}
          required
        />
      </Field>

      <Field label="Email">
        <input
          className="input"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </Field>

      <Field label="Password" hint="At least 8 characters">
        <input
          className="input"
          type="password"
          autoComplete="new-password"
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
      </Field>

      <button
        type="submit"
        className="btn btn--primary btn--block"
        disabled={busy}
      >
        {busy ? "Joining…" : "Join family"}
      </button>

      <p className="muted" style={{ textAlign: "center" }}>
        <button type="button" className="link-button" onClick={onCancel}>
          Back to sign in
        </button>
      </p>
    </form>
  );
}
